import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import BalanceRow from "../../components/leaves/BalanceRow";
import { diffDaysIST, todayIST } from "../../lib/datetime";
import { NO_VALUE, plural } from "../../lib/vocabulary";
import { SELF_SERVICE_ROLES } from "../../lib/roleOptions";
import { ArrowLeft, Send, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ApplyLeavePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  // BACKEND-SERVED vocabulary (the leaveOptions constants were the #78 drift
  // shape): types and policy come from /leaves/meta, and the DAY COUNT comes
  // from /leaves/days-preview — the same function the debit uses, so the form
  // and the charge cannot disagree (D-003).
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    leave_type: "CL",
    from_date: todayIST(),
    to_date: todayIST(),
    half_day: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Both manager tiers apply for their own leave — see SELF_SERVICE_ROLES.
    if (user && !SELF_SERVICE_ROLES.includes(user.role)) {
      navigate("/leaves", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    api
      .get(`/leaves/balance/${user.id}`)
      .then(({ data }) => setBalance(data))
      .catch(() => {});
    api
      .get("/leaves/meta")
      .then(({ data }) => setMeta(data))
      .catch(() => {
        // NO FALLBACK LIST (the #78 rule): if the vocabulary cannot be
        // loaded, submission is blocked and the error says so — a stale
        // local list is how every Sales Officer's expense once failed.
        setError("Could not load leave types — check your connection and reload.");
      });
  }, [user]);

  useEffect(() => {
    if (!form.from_date || !form.to_date || form.from_date > form.to_date) {
      setPreview(null);
      return;
    }
    let stale = false;
    api
      .get("/leaves/days-preview", {
        params: {
          from_date: form.from_date,
          to_date: form.to_date,
          half_day: form.half_day || undefined,
        },
      })
      .then(({ data }) => {
        if (!stale) setPreview(data);
      })
      .catch(() => {
        if (!stale) setPreview(null);
      });
    return () => {
      stale = true;
    };
  }, [form.from_date, form.to_date, form.half_day]);

  // MISSING IS MISSING (Pattern A). `?? 0` printed "Requesting 0 working
  // days" whenever the preview had not arrived or its fetch failed — a
  // confident zero standing in for an unknown, next to a warning that decides
  // whether the officer loses pay. `null` here, said as `—` below, and no LOP
  // arithmetic runs on a number nobody has.
  const days = preview?.days_count ?? null;
  const daysKnown = days !== null && days !== undefined;
  const typeBalance = balance?.[form.leave_type];
  const balanceKnown =
    typeBalance && typeBalance.balance !== null && typeBalance.balance !== undefined;
  const willLop = daysKnown && balanceKnown ? days > typeBalance.balance : false;
  const lopAmount = willLop ? Math.max(0, days - typeBalance.balance) : 0;
  const minSpell = meta?.policy?.pl_min_spell_days ?? 2;
  const plTooShort =
    form.leave_type === "PL" && daysKnown && days > 0 && days <= minSpell;

  function update(k, v) {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      // Half-day implies single day.
      if (k === "half_day" && v) next.to_date = next.from_date;
      // If single-day flips off, allow longer ranges.
      if (k === "from_date" && next.half_day) next.to_date = v;
      return next;
    });
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.reason.trim()) {
      setError("Please add a reason.");
      return;
    }
    // Inclusive calendar-day arithmetic from the ONE implementation:
    // 12 Aug -> 13 Aug is 2 days, and a half day only exists on 1.
    if (form.half_day && diffDaysIST(form.from_date, form.to_date) !== 1) {
      setError("Half-day only works for a single date.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        leave_type: form.leave_type,
        from_date: form.from_date,
        to_date: form.to_date,
        half_day: form.half_day || null,
        reason: form.reason.trim(),
      };
      const { data } = await api.post("/leaves", payload);
      toast.success("Leave applied — pending approval");
      navigate(`/leaves/${data.id}`, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="apply-leave-page">
      <Link
        to="/leaves"
        className="inline-flex items-center gap-1 text-sm text-callisto-muted hover:text-callisto-navy"
      >
        <ArrowLeft size={14} />
        Back
      </Link>
      <div className="mt-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold">
          Apply for leave
        </span>
        <h1 className="mt-1 text-2xl md:text-3xl font-bold text-callisto-navy">
          New leave request
        </h1>
        <p className="mt-1 text-sm text-callisto-muted">
          Your balance is shown below. Submit and your manager will review.
        </p>
      </div>

      {balance && (
        <div className="mt-5">
          <BalanceRow balance={balance} testIdPrefix="apply-balance" />
        </div>
      )}

      <form onSubmit={onSubmit} className="mt-6 bg-white border border-callisto rounded-xl p-5 md:p-7 space-y-5">
        <div>
          <Label className="text-callisto-navy font-medium">
            Leave type <span className="text-callisto-gold">*</span>
          </Label>
          <Select value={form.leave_type} onValueChange={(v) => update("leave_type", v)}>
            <SelectTrigger
              className="h-12 mt-1.5 focus:ring-callisto-gold"
              data-testid="apply-leave-type"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(meta?.leave_types || []).map((t) => (
                <SelectItem key={t} value={t}>
                  {meta?.type_labels?.[t] || t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.leave_type === "PL" && (
            <p className="mt-1 text-[11px] text-callisto-muted">
              PL is availed only in spells longer than {minSpell} working days.
              Sickness files under CL.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ld-from" className="text-callisto-navy font-medium">
              From <span className="text-callisto-gold">*</span>
            </Label>
            <Input
              id="ld-from"
              type="date"
              value={form.from_date}
              onChange={(e) => update("from_date", e.target.value)}
              className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="apply-leave-from"
            />
          </div>
          <div>
            <Label htmlFor="ld-to" className="text-callisto-navy font-medium">
              To <span className="text-callisto-gold">*</span>
            </Label>
            <Input
              id="ld-to"
              type="date"
              min={form.from_date}
              value={form.to_date}
              onChange={(e) => update("to_date", e.target.value)}
              disabled={!!form.half_day}
              className="h-12 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="apply-leave-to"
            />
          </div>
        </div>

        <div>
          <Label className="text-callisto-navy font-medium">
            Half day <span className="text-callisto-muted font-normal">(only for single date)</span>
          </Label>
          <Select value={form.half_day || "__none__"} onValueChange={(v) => update("half_day", v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-12 mt-1.5 focus:ring-callisto-gold" data-testid="apply-leave-halfday">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Full day</SelectItem>
              {(meta?.half_day_options || [])
                .filter((o) => o.value)
                .map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="reason" className="text-callisto-navy font-medium">
            Reason <span className="text-callisto-gold">*</span>
          </Label>
          <Textarea
            id="reason"
            rows={3}
            value={form.reason}
            onChange={(e) => update("reason", e.target.value)}
            placeholder="Tell your manager why you need this leave"
            className="mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
            data-testid="apply-leave-reason"
          />
        </div>

        {/* Summary bar — the day count is the SERVER's working-day answer,
            the same one the debit will use. */}
        <div className="rounded-lg border border-callisto bg-callisto-app px-3 py-2.5 text-sm flex items-center justify-between flex-wrap gap-2">
          <span className="text-callisto-muted" data-testid="apply-days-summary">
            Requesting{" "}
            <span className="font-bold text-callisto-navy">
              {daysKnown ? plural(days, "working day") : NO_VALUE}
            </span>{" "}
            of{" "}
            <span className="font-bold text-callisto-navy">{form.leave_type}</span>
            {preview?.non_working_in_span > 0 && (
              <span className="text-callisto-muted">
                {" "}
                ({plural(preview.non_working_in_span, "Sunday/holiday")} in the
                span cost nothing)
              </span>
            )}
          </span>
          {typeBalance && (
            <span className="text-callisto-muted">
              Balance:{" "}
              <span className="font-bold text-callisto-navy">
                {balanceKnown ? typeBalance.balance : NO_VALUE}
              </span>
            </span>
          )}
        </div>

        {daysKnown && days === 0 && (
          <div
            className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm"
            data-testid="apply-no-working-days"
          >
            Those dates contain no working days — Sundays and declared holidays
            are not leave days.
          </div>
        )}

        {plTooShort && (
          <div
            className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 px-3 py-2 text-sm"
            data-testid="apply-pl-spell-warning"
          >
            PL is availed only in spells longer than {minSpell} working days —
            this span has {days}. Use CL for short absences.
          </div>
        )}

        {willLop && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2 text-sm flex items-start gap-2" data-testid="apply-lop-warning">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <p>
              Your {form.leave_type} balance is {typeBalance.balance}.{" "}
              <span className="font-bold">{plural(lopAmount, "day")}</span> will
              be marked as Loss of Pay (LOP) upon approval.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/leaves")}
            className="h-11 border-callisto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold sm:min-w-32"
            data-testid="apply-leave-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-1.5" /> Submit request
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
